import React, { useState, useEffect } from 'react';

import { Tooltip as AntdTooltip } from 'antd';
import queryString from 'query-string';
import { BounceLoader } from 'react-spinners';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  getCompanyProfileSymbolData,
  getRecommendationSymbolData,
} from '../../graphql';
import { formatNumber2, green } from '../../utils';
import { formatNumberWithPercent } from '../../utils/helpers';
import { DayTrend } from '../index';
import styles from '../searchReport/style.module.scss';
// import News from './NewsPage';
import './News.css';

interface Props {
  symbolFromISR: String;
  fieldType: String;
  priceFromIsr: String;
}

const StackedBarChart: React.FC<Props> = ({
  history,
  symbolFromISR,
  fieldType,
  priceFromIsr,
}) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState(0);
  const [previousPrice, setPreviousPrice] = useState(0);
  const [priceDifference, setPriceDifference] = useState(0);
  const [percentageChange, setPercentageChange] = useState(0);
  const [retries, setRetries] = useState(0);
  const [companyProfile, setCompanyProfile] = useState({});
  const [socket, setSocket] = useState(null);
  const socketUrl = `wss://ws.finnhub.io?token=cnsr81hr01qt1mk8sdk0cnsr81hr01qt1mk8sdkg`;
  const symbol = symbolFromISR; /*window.location.href.split('/').pop();*/
  const initializeSocket = () => {
    const socketInstance = new WebSocket(socketUrl);
    let dataReceived = false;

    socketInstance.addEventListener('open', function () {
      console.log('WebSocket connection established');
      socketInstance.send(
        JSON.stringify({ type: 'subscribe', symbol: symbol }),
      );
      setLoading(false);
    });

    socketInstance.addEventListener('message', function (event) {
      const data = JSON.parse(event.data);
      console.log('WebSocket Data:', data);

      if (data.type === 'trade') {
        const latestPrice = data.data[0]?.p;
        if (latestPrice) {
          dataReceived = true;
          setPrice(latestPrice);
        }
      }
    });

    socketInstance.addEventListener('error', function (error) {
      console.error('WebSocket error');
    });
    socketInstance.addEventListener('close', function () {
      console.log('WebSocket connection closed');
      if (!dataReceived) {
        setPrice(Number(priceFromIsr));
      }

      // Retry logic with exponential backoff
      if (retries < 5) {
        const retryDelay = Math.pow(2, retries) * 3000; // Exponential backoff
        setRetries((prev) => prev + 1); // Increment retries after calculating delay

        console.log(
          `Reconnecting in ${retryDelay / 1000} seconds... Attempt ${
            retries + 1
          }`,
        );
        setTimeout(() => initializeSocket(), retryDelay);
      } else {
        console.log('WebSocket connection failed after multiple attempts');
      }
    });

    setSocket(socketInstance);

    const pingInterval = setInterval(() => {
      if (socketInstance.readyState === WebSocket.OPEN) {
        socketInstance.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds

    // Cleanup function for component unmount or socket closure
    return () => {
      clearInterval(pingInterval); // Cleanup ping interval
      if (socketInstance) {
        socketInstance.close(); // Close WebSocket connection
      }
    };
  };
  const CustomTooltip = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      const barColors = {
        buy: '#8884d8',
        hold: '#82ca9d',
        sell: '#ffc658',
        strongBuy: 'green',
        strongSell: '#ff6666',
      };

      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: 'white',
            padding: '10px',
            border: '1px solid #ccc',
          }}
        >
          <p style={{ color: 'black' }}>{`Date: ${data.date}`}</p>
          {['buy', 'hold', 'sell', 'strongBuy', 'strongSell'].map((key) => (
            <p
              style={{ color: 'black', display: 'flex', alignItems: 'center' }}
              key={key}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  marginRight: '10px',
                  backgroundColor: barColors[key],
                }}
              ></span>
              {`${key.charAt(0).toUpperCase() + key.slice(1)}: ${data[key]}`}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };

  // Fetch chart data and last price on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `https://finnhub.io/api/v1/stock/recommendation?symbol=${symbol}`;
        const profileUrl = `https://finnhub.io/api/v1/stock/profile?symbol=${symbol}`;
        const response = await getRecommendationSymbolData(url);
        const response1 = await getCompanyProfileSymbolData(profileUrl);

        const apiData = response.recommendationData;
        const apiProfileData = response1?.companyProfileData;
        setCompanyProfile(apiProfileData);
        console.log(companyProfile);
        const currentDate = new Date();
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(currentDate.getMonth() - 8);

        const filteredData = apiData.filter((item) => {
          const periodDate = new Date(item.period);
          return periodDate >= threeMonthsAgo && periodDate <= currentDate;
        });

        const uniqueDates = new Set();
        const formattedData = filteredData
          .filter((item) => {
            const periodDate = new Date(item.period).toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              },
            );

            if (!uniqueDates.has(periodDate)) {
              uniqueDates.add(periodDate);
              return true;
            }
            return false;
          })
          .map((item) => ({
            period: new Date(item.period).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
            }),
            date: new Date(item.period).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }),
            buy: item.buy,
            hold: item.hold,
            sell: item.sell,
            strongBuy: item.strongBuy,
            strongSell: item.strongSell,
          }));

        setChartData(formattedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data', error);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (socket) {
        socket.close(); // Cleanup socket on unmount
      }
    };
  }, [symbol]);

  // Handle price difference and percentage change calculation
  useEffect(() => {
    if (price && previousPrice) {
      const difference = price - previousPrice;
      const percentage = ((difference / previousPrice) * 100).toFixed(2);
      setPriceDifference(difference.toFixed(2));
      setPercentageChange(percentage);
    }
    if (price !== previousPrice) {
      setPreviousPrice(price);
    }
  }, [price]);
  useEffect(() => {
    initializeSocket();
  }, [retries]);

  if (loading) {
    return (
      <div className={styles.miniLoaderStyle}>
        <BounceLoader color={green} loading={loading} size={50} />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight:
          fieldType === 'recommendation' && chartData.length !== 0
            ? '50vh'
            : chartData.length === 0 && fieldType === 'recommendation'
            ? '20vh'
            : '70vh',
        display: 'flex',
        marginLeft: fieldType === 'recommendation' ? '' : '100px',
        flexDirection: fieldType === 'recommendation' ? '' : 'column',
      }}
    >
      <div
        hidden={fieldType === 'recommendation'}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '1.5rem 4rem',
          marginBottom: '40px',
        }}
      >
        {/* Left Section - Company Info & Market Details */}
        <div style={{ flex: 1 }}>
          {/* Company Name & Sector */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: '-20px',
            }}
          >
            <h1
              style={{
                color: '#2FC058',
                cursor: 'pointer',
                paddingTop: '20px',
                fontSize: '2.8rem',
                fontWeight: 'bold',
              }}
              onClick={() => {
                let filters = {
                  type: 'symbol',
                  subtype: 'individual_statistical_report',
                  symbols: symbol,
                };
                history.push(
                  `/search/report/` + queryString.stringify(filters),
                );
              }}
            >
              {symbol}
              <span
                style={{
                  color: 'var(--whitecolor) !important',
                  fontSize: '1rem', // Adjusted for correct size
                  marginLeft: '15px',
                }}
              >
                {companyProfile['name'] ? companyProfile['name'] : ''}
              </span>
            </h1>
            <p
              style={{
                fontSize: '2.5rem',
                paddingLeft: '45px',
                paddingTop: '25px',
                fontWeight: 'bold',
                color: 'var(--whitecolor)',
                marginRight: '10px', // Adds spacing between price and change info
              }}
            >
              ${price || priceFromIsr}
            </p>
            {loading ? (
              <p>Loading...</p>
            ) : priceDifference !== 0 ? (
              <div
                style={{
                  fontSize: '1.3rem',
                  textAlign: 'left',
                  color: priceDifference > 0 ? 'green' : 'red',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    fontWeight: 'bold',
                    marginTop: '20px',
                  }}
                >
                  {priceDifference > 0
                    ? `+${priceDifference}`
                    : priceDifference}
                  {priceDifference > 0 ? '▲' : '▼'}
                </span>
                <span style={{ display: 'block', marginBottom: '0px' }}>
                  {priceDifference > 0
                    ? `+${percentageChange}%`
                    : `${percentageChange}%`}
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
          <p
            style={{
              color: 'var(--whitecolor) !important',
              fontSize: '1.2rem',
              marginTop: '-50px',
            }}
          >
            {companyProfile['gsector'] ? companyProfile['gsector'] + ' | ' : ''}
            <span>
              {companyProfile['gsubind'] ? companyProfile['gsubind'] : ''}
            </span>
          </p>

          {/* Market Data (Aligned Below) */}
          <div
            style={{
              marginTop: '10px',
              padding: '5px',
              borderRadius: '6px',
              border: '1px solid #444',
              color: 'var(--whitecolor)',
              fontSize: '0.85rem',
              width: 'fit-content',
              maxWidth: '100%',
              position: 'relative',
              overflow: 'visible',
              zIndex: 0,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, auto)',
                alignItems: 'center',
                gap: '5px 0px',
                fontWeight: 600,
                borderBottom: '1px solid #555',
                paddingBottom: '4px',
              }}
            >
              {[
                ['M C', 'Market Capitalization'],
                ['S O', 'Shares Outstanding'],
                ['F S', 'Floating Shares'],
                ['I O', 'Institution Ownership'],
                ['In O', 'Insider Ownership'],
              ].map(([abbr, full], index) => (
                <AntdTooltip title={full} key={abbr}>
                  <div
                    style={{
                      cursor: 'pointer',
                      padding: '4px 12px',
                      borderRight: index < 4 ? '1px solid #444' : 'none',
                    }}
                  >
                    {abbr}
                  </div>
                </AntdTooltip>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, auto)',
                gap: '8px 0px',
                paddingTop: '6px',
              }}
            >
              {[
                `$${formatNumber2(
                  Number(companyProfile?.marketCapitalization) * 1e6,
                  0,
                  true,
                )}`,
                formatNumber2(
                  Number(companyProfile?.shareOutstanding) * 1e6,
                  0,
                  true,
                ),
                formatNumber2(
                  Number(companyProfile?.floatingShare) * 1e6,
                  0,
                  true,
                ),
                formatNumberWithPercent(companyProfile?.institutionOwnership),
                formatNumberWithPercent(companyProfile?.insiderOwnership),
              ].map((val, index) => (
                <div
                  key={index}
                  style={{
                    padding: '4px 12px',
                    borderRight: index < 4 ? '1px solid #444' : 'none',
                  }}
                >
                  {val}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Section - Stock Price */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingRight: '320px',
            justifyContent: 'center',
            minWidth: '260px', // Adjusted width
          }}
        ></div>
      </div>

      <div
        style={{
          marginTop: '-200px',
        }}
      >
        <DayTrend
          isHomeSearch={true}
          symbol={symbolFromISR}
          fieldType={fieldType}
        />
      </div>
      {chartData.length > 0 ? (
        <div
          hidden={fieldType === 'summary'}
          style={{
            width: '60%',
            height: 400,
            // marginTop: '200px !important',
            // marginBottom: '200px !important',
            margin: 'auto',
          }}
        >
          {/*<h1*/}
          {/*  style={{*/}
          {/*    color: '#1abd50',*/}
          {/*    margin: 'auto',*/}
          {/*    paddingBottom: '30px',*/}
          {/*    paddingLeft: 200,*/}
          {/*  }}*/}
          {/*>*/}
          {/*  Analyst Recommendations*/}
          {/*</h1>*/}
          <ResponsiveContainer width="95%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="buy" stackId="a" fill="#8884d8" barSize={20} />
              <Bar dataKey="hold" stackId="a" fill="#82ca9d" barSize={20} />
              <Bar dataKey="sell" stackId="a" fill="#ffc658" barSize={20} />
              <Bar dataKey="strongBuy" stackId="a" fill="green" barSize={20} />
              <Bar
                dataKey="strongSell"
                stackId="a"
                fill="#ff6666"
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : chartData.length === 0 ? (
        <div
          hidden={fieldType === 'summary'}
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: 'auto',
            fontSize: '30px',
            color: 'var(--whitecolor)',
            textAlign: 'center',
          }}
        >
          No Recommendations for {symbol}
        </div>
      ) : (
        <div className={styles.miniLoaderStyle}>
          <BounceLoader color={green} loading={loading} size={50} />
        </div>
      )}
      {/*<div hidden={true} style={{ flex: 1, marginTop: 100 }}>*/}
      {/*  <News symbol={symbol} />*/}
      {/*</div>*/}
    </div>
  );
};

export default StackedBarChart;
